/**
 * Which role does the sidebar badge show? (user request 2026-10-08)
 *
 * The badge used to print `users.role` — the technical write-guard ceiling. A real
 * member saw « read-only » next to his name while he was the band's TECHNICIAN:
 * technically true (the account cannot write outside its bands) and completely
 * confusing as a label.
 *
 * The rule: show what the person IS, in this order —
 *   1. the shared demo account (`demo`),
 *   2. the platform administrator (`superadmin`),
 *   3. the role held in the ACTIVE band (owner / editor / tech / viewer),
 *   4. nothing at all when the account has no band role to show (rather than a
 *      blanket « read-only » that says nothing about what the person can do).
 *
 * Kept as a pure function so it is testable: the label lives in a JSX ternary
 * otherwise, and a visibility rule hidden in JSX is a rule nobody can test.
 */
export interface RoleBadgeInput {
  isShowcase?: boolean | null;
  /** `users.global_role` — 'superadmin' | 'user'. */
  globalRole?: string | null;
  /** `my_role` on the ACTIVE band — 'owner' | 'editor' | 'tech' | 'viewer' | null. */
  bandRole?: string | null;
}

/** The `roles.*` i18n key to display, or null when nothing should be shown. */
export function roleBadgeKey(input: RoleBadgeInput): string | null {
  if (input.isShowcase) return 'roles.demo';
  if (input.globalRole === 'superadmin') return 'roles.superadmin';
  const role = input.bandRole;
  if (role === 'owner' || role === 'editor' || role === 'tech' || role === 'viewer') {
    return `roles.${role}`;
  }
  return null;
}

/** CSS suffix of the badge (`role-<x>`), derived from the same key. */
export function roleBadgeClass(input: RoleBadgeInput): string | null {
  const key = roleBadgeKey(input);
  return key ? key.replace('roles.', 'role-') : null;
}
